type MealSlotTarget = {
  mealOrder: number;
  mealName: string;
  targetCalories: number;
};

type MealSlotTargetsSummary = {
  totalCalories: number;
  breakdown: MealSlotTarget[];
  text: string;
};

type MealLike = {
  mealOrder?: unknown;
  mealName?: unknown;
  name?: unknown;
  mealType?: unknown;
  targetCalories?: unknown;
  targetKcal?: unknown;
  calories?: unknown;
};

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const toNormalizedLabel = (value: string): string =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const toDisplayMealName = (meal: MealLike, fallbackIndex: number): string => {
  const rawName =
    (typeof meal.mealName === 'string' && meal.mealName.trim()) ||
    (typeof meal.name === 'string' && meal.name.trim()) ||
    (typeof meal.mealType === 'string' && meal.mealType.trim()) ||
    `Meal ${fallbackIndex}`;

  return toNormalizedLabel(rawName);
};

const toMealsArray = (program: unknown): MealLike[] => {
  if (!program) return [];
  if (Array.isArray(program)) {
    return program.filter((item): item is MealLike => !!item && typeof item === 'object');
  }

  if (typeof program === 'object') {
    const maybeMeals = (program as { meals?: unknown }).meals;
    if (Array.isArray(maybeMeals)) {
      return maybeMeals.filter((item): item is MealLike => !!item && typeof item === 'object');
    }
  }

  return [];
};

const extractMealTargets = (searchParams: unknown): MealSlotTarget[] => {
  if (!searchParams || typeof searchParams !== 'object') {
    return [];
  }

  const params = searchParams as {
    overrideMealProgram?: unknown;
    mealProgram?: unknown;
  };

  const overrideMeals = toMealsArray(params.overrideMealProgram);
  const baseMeals = toMealsArray(params.mealProgram);
  const sourceMeals = overrideMeals.length > 0 ? overrideMeals : baseMeals;

  return sourceMeals
    .map((meal, index) => {
      const targetCalories = toFiniteNumber(meal.targetCalories ?? meal.targetKcal ?? meal.calories);
      if (!targetCalories || targetCalories <= 0) {
        return null;
      }

      return {
        mealOrder: toFiniteNumber(meal.mealOrder) ?? index + 1,
        mealName: toDisplayMealName(meal, index + 1),
        targetCalories,
      } as MealSlotTarget;
    })
    .filter((target): target is MealSlotTarget => target !== null)
    .sort((a, b) => a.mealOrder - b.mealOrder);
};

export const buildMealSlotTargetsSummary = (
  searchParams: unknown
): MealSlotTargetsSummary | null => {
  const breakdown = extractMealTargets(searchParams);
  if (!breakdown.length) {
    return null;
  }

  const totalCalories = Math.round(
    breakdown.reduce((sum, meal) => sum + meal.targetCalories, 0)
  );

  const text = `${totalCalories} kcal (${breakdown
    .map((meal) => `${Math.round(meal.targetCalories)} ${meal.mealName}`)
    .join(' + ')})`;

  return {
    totalCalories,
    breakdown,
    text,
  };
};

export type { MealSlotTarget, MealSlotTargetsSummary };
